// src/components/ReportSaudeScreen.jsx
// Receita Saúde premium.
// Rota: /reports/saude
// Endpoint: GET /psy/report/receita-saude?id=ID_USUARIO&dataInicio=YYYY-MM-DD&dataFim=YYYY-MM-DD
// Usa payload com sessoes[] por cliente, modal de detalhes e geração de PDF via print/iframe.
// Ajustado para exibir CPF do cliente e CPF do pagador separadamente.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  CircleDollarSign,
  FileText,
  Menu,
  RefreshCw,
  Search,
  ShieldCheck,
  Users,
  X,
} from "lucide-react";

import AppSidebar from "./AppSidebar";
import Logo from "../assets/logoClaro.webp";
import { useToast } from "../context/ToastContext";
import "../sass/pages/report-saude.scss";
import { API_BASE as CONFIG_API_BASE } from "../config/apiBase";

const API_BASE = (
  CONFIG_API_BASE ||
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
  USER_NAME: "psy_usuario",
};

function pad2(value) {
  return String(value).padStart(2, "0");
}

function todayISO() {
  const date = new Date();
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function monthStartISO() {
  const date = new Date();
  const first = new Date(date.getFullYear(), date.getMonth(), 1);

  return `${first.getFullYear()}-${pad2(first.getMonth() + 1)}-${pad2(first.getDate())}`;
}

function isValidISODate(value) {
  const raw = String(value || "").trim();

  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return false;

  const [year, month, day] = raw.split("-").map(Number);
  const date = new Date(year, month - 1, day);

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

function parseISODateLocal(value) {
  if (!isValidISODate(value)) return null;

  const [year, month, day] = String(value).split("-").map(Number);
  return new Date(year, month - 1, day, 0, 0, 0, 0);
}

function readStorage(keys = []) {
  for (const key of keys) {
    const value = sessionStorage.getItem(key) || localStorage.getItem(key);

    if (value && String(value).trim() !== "") {
      return value;
    }
  }

  return "";
}

function saveAuthToken(token) {
  if (!token) return;

  localStorage.setItem(AUTH_KEYS.TOKEN, token);
  sessionStorage.setItem(AUTH_KEYS.TOKEN, token);
}

function normalizeText(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function safeNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function brl(value) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(safeNumber(value));
}

function formatDateBR(value) {
  if (!value) return "Não informado";

  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) return "Não informado";

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function formatDateTimeBR(value) {
  if (!value) return "Não informado";

  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) return "Não informado";

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getTimeBR(value) {
  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) return "--:--";

  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getFirstName(name = "") {
  const clean = String(name || "").trim();

  if (!clean) return "";

  return clean.split(/\s+/)[0];
}

function getInitials(name = "") {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return "P";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function onlyDigits(value = "") {
  return String(value || "").replace(/\D/g, "");
}

function formatCPF(value = "") {
  const digits = onlyDigits(value).slice(0, 11);

  if (digits.length !== 11) return value || "N/A";

  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

function normalizeCpfValue(...values) {
  for (const value of values) {
    const raw = String(value || "").trim();

    if (raw && raw !== "N/A" && raw !== "—") {
      return raw;
    }
  }

  return "N/A";
}

function resolveCpfCliente(row = {}) {
  return normalizeCpfValue(
    row?.cpfCliente,
    row?.cpfPaciente,
    row?.cpf,
    row?.documentoCliente,
    row?.documento,
    row?.clienteCpf
  );
}

function resolveCpfPagador(row = {}, cpfCliente = "N/A") {
  return normalizeCpfValue(
    row?.cpfPagador,
    row?.cpfResponsavel,
    row?.cpfDoPagador,
    row?.pagadorCpf,
    row?.responsavelCpf,
    cpfCliente
  );
}

function resolvePagadorEhCliente(row = {}, cpfCliente = "N/A", cpfPagador = "N/A") {
  if (typeof row?.pagadorEhCliente === "boolean") {
    return row.pagadorEhCliente;
  }

  const clienteDigits = onlyDigits(cpfCliente);
  const pagadorDigits = onlyDigits(cpfPagador);

  if (clienteDigits.length === 11 && pagadorDigits.length === 11) {
    return clienteDigits === pagadorDigits;
  }

  return false;
}

function buildCpfLabel(row = {}, separator = " • ") {
  const cpfCliente = formatCPF(row.cpfCliente || "N/A");
  const cpfPagador = formatCPF(row.cpfPagador || "N/A");

  if (row.pagadorEhCliente) {
    return `CPF cliente/pagador: ${cpfCliente}`;
  }

  return [`CPF cliente: ${cpfCliente}`, `CPF pagador: ${cpfPagador}`].join(
    separator
  );
}

function safeHTML(value = "") {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function normalizeRows(payload) {
  if (!Array.isArray(payload)) return [];

  return payload.map((row, index) => {
    const cpfCliente = resolveCpfCliente(row);
    const cpfPagador = resolveCpfPagador(row, cpfCliente);
    const pagadorEhCliente = resolvePagadorEhCliente(
      row,
      cpfCliente,
      cpfPagador
    );

    return {
      id: String(row?.clienteId || row?._id || row?.id || index),
      clienteId: String(row?.clienteId || ""),
      nome: String(row?.nome || "Sem nome").trim(),

      cpfCliente,
      cpfPagador,
      pagadorEhCliente,

      totalSessoes: safeNumber(row?.totalSessoes, 0),
      valorTotalPago: safeNumber(row?.valorTotalPago, 0),
      tipoPred: String(row?.tipoSessaoPredominante || row?.tipoPred || "—").trim(),
      sessoes: Array.isArray(row?.sessoes) ? row.sessoes : [],
    };
  });
}

function normalizeSession(session = {}, index = 0) {
  return {
    id: session?.id || session?._id || `${session?.dataSessao || "sessao"}-${index}`,
    dataSessao: session?.dataSessao || session?.data || session?.createdAt || null,
    tipoSessao: String(session?.tipoSessao || session?.tipo || "—").trim(),
    status: String(session?.status || "—").trim(),
    duracao: safeNumber(session?.duracao ?? session?.duracaoMin ?? 0),
    valor: safeNumber(session?.valor ?? session?.valorSessao ?? 0),
    servico: String(session?.servico || "").trim(),
  };
}

function buildRowSearchText(row) {
  return normalizeText(
    [
      row.nome,
      row.cpfCliente,
      formatCPF(row.cpfCliente),
      row.cpfPagador,
      formatCPF(row.cpfPagador),
      row.pagadorEhCliente ? "pagador cliente" : "pagador diferente",
      row.tipoPred,
      String(row.totalSessoes),
      brl(row.valorTotalPago),
    ]
      .filter(Boolean)
      .join(" ")
  );
}

async function readApiError(response) {
  try {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      const payload = await response.json();
      return payload?.message || payload?.error || JSON.stringify(payload);
    }

    const text = await response.text();
    return text || `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}

function getPeriodLabel(startISO, endISO) {
  return `${formatDateBR(`${startISO}T00:00:00`)} até ${formatDateBR(`${endISO}T00:00:00`)}`;
}

function HealthCard({ total, sessions }) {
  const hasData = sessions > 0;

  return (
    <aside
      className={`saude-hero-card ${
        hasData ? "saude-hero-card--good" : "saude-hero-card--empty"
      }`}
    >
      <div className="saude-hero-card__icon" aria-hidden="true">
        {hasData ? (
          <CheckCircle2 size={22} />
        ) : (
          <FileText size={22} />
        )}
      </div>

      <div>
        <small>Resumo do período</small>
        <strong>{hasData ? brl(total) : "Sem dados"}</strong>
        <span>
          {hasData
            ? `${sessions} sessão(ões) encontradas para Receita Saúde.`
            : "Nenhuma sessão encontrada no período selecionado."}
        </span>
      </div>
    </aside>
  );
}

function MetricCard({
  icon: Icon,
  label,
  value,
  description,
  tone = "blue",
}) {
  return (
    <article className={`saude-kpi saude-kpi--${tone}`}>
      <div className="saude-kpi__icon" aria-hidden="true">
        <Icon size={19} />
      </div>

      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{description}</small>
      </div>
    </article>
  );
}

function ReportSkeleton() {
  return (
    <div className="saude-skeleton" aria-hidden="true">
      <div className="saude-skeleton__hero" />
      <div className="saude-skeleton__kpis">
        <span />
        <span />
        <span />
      </div>
      <div className="saude-skeleton__panel" />
      <div className="saude-skeleton__list">
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}

function EmptyState({
  title,
  message,
  onRetry,
  onBack,
  isError = false,
}) {
  return (
    <section
      className={`saude-state ${
        isError ? "saude-state--error" : ""
      }`}
    >
      <div className="saude-state__icon" aria-hidden="true">
        {isError ? (
          <AlertCircle size={24} />
        ) : (
          <FileText size={24} />
        )}
      </div>

      <strong>{title}</strong>
      <span>{message}</span>

      <div className="saude-state__actions">
        <button
          className="saude-btn saude-btn--ghost-dark"
          type="button"
          onClick={onBack}
        >
          <ArrowLeft size={16} />
          Central financeira
        </button>

        <button
          className="saude-btn saude-btn--primary"
          type="button"
          onClick={onRetry}
        >
          <RefreshCw size={16} />
          Buscar novamente
        </button>
      </div>
    </section>
  );
}

function DetailsModal({
  open,
  client,
  dataInicio,
  dataFim,
  onClose,
}) {
  const sessions = useMemo(() => {
    const list = Array.isArray(client?.sessoes) ? client.sessoes : [];
    return list.map(normalizeSession);
  }, [client]);

  if (!open) return null;

  return (
    <div
      className="saude-modal-overlay"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <section className="saude-modal" onClick={(event) => event.stopPropagation()}>
        <header className="saude-modal__head">
          <div>
            <span>Detalhes do paciente</span>
            <h2>{client?.nome || "Detalhes"}</h2>
            <p>
              {buildCpfLabel(client, " • ")} • Período:{" "}
              {getPeriodLabel(dataInicio, dataFim)}
            </p>
          </div>

          <button type="button" onClick={onClose} aria-label="Fechar">
            <X size={18} />
          </button>
        </header>

        <div className="saude-modal__summary">
          <span>
            Sessões <b>{safeNumber(client?.totalSessoes)}</b>
          </span>
          <span>
            Total pago <b>{brl(client?.valorTotalPago)}</b>
          </span>
          <span>
            Tipo predominante <b>{client?.tipoPred || "—"}</b>
          </span>
        </div>

        <div className="saude-table-wrap">
          <table className="saude-table">
            <thead>
              <tr>
                <th>Data/Hora</th>
                <th>Tipo</th>
                <th>Status</th>
                <th className="is-right">Duração</th>
                <th className="is-right">Valor</th>
                <th>Serviço</th>
              </tr>
            </thead>

            <tbody>
              {sessions.length ? (
                sessions.map((session) => (
                  <tr key={session.id}>
                    <td>{formatDateTimeBR(session.dataSessao)}</td>
                    <td>{session.tipoSessao || "—"}</td>
                    <td>
                      <span className="saude-status">{session.status || "—"}</span>
                    </td>
                    <td className="is-right">{session.duracao} min</td>
                    <td className="is-right">{brl(session.valor)}</td>
                    <td>{session.servico || "—"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6}>Sem sessões no período.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <footer className="saude-modal__footer">
          <button className="saude-btn saude-btn--primary" type="button" onClick={onClose}>
            Fechar
          </button>
        </footer>
      </section>
    </div>
  );
}

export default function ReportSaudeScreen() {
  const navigate = useNavigate();
  const toast = useToast();

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [dataInicio, setDataInicio] = useState(monthStartISO());
  const [dataFim, setDataFim] = useState(todayISO());

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [somentePagas, setSomentePagas] = useState(true);
  const [tipoSessao, setTipoSessao] = useState("todos");

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsClient, setDetailsClient] = useState(null);

  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const nomeUsuario = useMemo(() => readStorage([AUTH_KEYS.USER_NAME]) || "", []);
  const firstName = useMemo(() => getFirstName(nomeUsuario), [nomeUsuario]);

  const authOrRedirect = useCallback(() => {
    const token = readStorage([AUTH_KEYS.TOKEN, "token"]);
    const userId = readStorage([AUTH_KEYS.USER_ID, "id"]);

    if (!userId) {
      toast.error({
        title: "Sessão inválida",
        message: "ID do usuário não encontrado. Faça login novamente.",
        duration: 6500,
      });
      navigate("/login", { replace: true });
      return null;
    }

    if (!token) {
      toast.error({
        title: "Sessão encerrada",
        message: "Token não encontrado. Faça login novamente.",
        duration: 6500,
      });
      navigate("/login", { replace: true });
      return null;
    }

    return { token, userId };
  }, [navigate, toast]);

  const validatePeriod = useCallback(() => {
    if (!isValidISODate(dataInicio) || !isValidISODate(dataFim)) {
      return "Período inválido.";
    }

    const start = parseISODateLocal(dataInicio);
    const end = parseISODateLocal(dataFim);

    if (!start || !end || end.getTime() < start.getTime()) {
      return "Data final não pode ser menor que a data inicial.";
    }

    return "";
  }, [dataInicio, dataFim]);

  const carregar = useCallback(
    async ({ signal, silent = false } = {}) => {
      const periodError = validatePeriod();

      if (periodError) {
        setError(periodError);
        setRows([]);
        return;
      }

      const auth = authOrRedirect();

      if (!auth) {
        setLoading(false);
        return;
      }

      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      try {
        const url =
          `${API_BASE}/psy/report/receita-saude` +
          `?id=${encodeURIComponent(auth.userId)}` +
          `&dataInicio=${encodeURIComponent(dataInicio)}` +
          `&dataFim=${encodeURIComponent(dataFim)}` +
          `&legacy=1` +
          `&somentePagas=${encodeURIComponent(String(somentePagas))}` +
          (tipoSessao && tipoSessao !== "todos" ? `&tipoSessao=${encodeURIComponent(tipoSessao)}` : "");

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${auth.token}`,
            "Content-Type": "application/json",
          },
          signal,
        });

        saveAuthToken(response.headers.get("x-new-token"));

        if (response.status === 401 || response.status === 403) {
          toast.error({
            title: "Sessão encerrada",
            message: "Sua sessão expirou. Faça login novamente.",
            duration: 6500,
          });
          navigate("/login", { replace: true });
          return;
        }

        if (!response.ok) {
          const message = await readApiError(response);
          setRows([]);
          setError(message || `Erro ao buscar relatório (${response.status}).`);
          return;
        }

        const payload = await response.json().catch(() => null);

        if (!Array.isArray(payload)) {
          setRows([]);
          setError("Formato de resposta inesperado.");
          return;
        }

        setRows(normalizeRows(payload));
        setLastUpdatedAt(new Date());
      } catch (err) {
        if (err?.name === "AbortError") return;

        console.error("Erro ao carregar Receita Saúde:", err);
        setRows([]);
        setError("Falha de comunicação com a API.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [
      authOrRedirect,
      dataFim,
      dataInicio,
      navigate,
      somentePagas,
      tipoSessao,
      toast,
      validatePeriod,
    ]
  );

  useEffect(() => {
    const controller = new AbortController();
    carregar({ signal: controller.signal });

    return () => controller.abort();
  }, [carregar]);

  const filteredRows = useMemo(() => {
    const query = normalizeText(search);

    if (!query) return rows;

    return rows.filter((row) => buildRowSearchText(row).includes(query));
  }, [rows, search]);

  const totals = useMemo(() => {
    const totalSessoes = rows.reduce(
      (sum, row) => sum + safeNumber(row.totalSessoes),
      0
    );
    const totalValor = rows.reduce(
      (sum, row) => sum + safeNumber(row.valorTotalPago),
      0
    );
    const mediaPorSessao = totalSessoes > 0 ? totalValor / totalSessoes : 0;

    return {
      clientes: rows.length,
      totalSessoes,
      totalValor,
      mediaPorSessao,
    };
  }, [rows]);

  const filteredTotals = useMemo(() => {
    const sessoes = filteredRows.reduce(
      (sum, row) => sum + safeNumber(row.totalSessoes),
      0
    );
    const valor = filteredRows.reduce(
      (sum, row) => sum + safeNumber(row.valorTotalPago),
      0
    );

    return { sessoes, valor };
  }, [filteredRows]);

  const openDetails = (row) => {
    if (!row) return;

    setDetailsClient(row);
    setDetailsOpen(true);
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setDetailsClient(null);
  };

  const reload = () => carregar({ silent: true });
  const goBack = () => navigate("/finance");

  const exportarPDF = () => {
    if (!rows.length) return;

    const title = "Relatório Receita Saúde";
    const periodo = `${formatDateBR(`${dataInicio}T00:00:00`)} até ${formatDateBR(`${dataFim}T00:00:00`)}`;
    const generatedAt = formatDateTimeBR(new Date());

    const rowsHtml = rows
      .map((row) => {
        const cpfLabel = buildCpfLabel(row, " • ");

        const sessoesHtml = (row.sessoes || [])
          .map((sessionRaw, index) => {
            const session = normalizeSession(sessionRaw, index);

            return `
              <tr>
                <td>${safeHTML(formatDateTimeBR(session.dataSessao))}</td>
                <td>${safeHTML(session.tipoSessao || "—")}</td>
                <td>${safeHTML(session.status || "—")}</td>
                <td style="text-align:right;">${safeHTML(session.duracao)} min</td>
                <td style="text-align:right;">${safeHTML(brl(session.valor))}</td>
                <td>${safeHTML(session.servico || "—")}</td>
              </tr>
            `;
          })
          .join("");

        return `
          <section class="card">
            <div class="cardHead">
              <div>
                <div class="name">${safeHTML(row.nome)}</div>
                <div class="meta">
                  ${safeHTML(cpfLabel)} • Tipo: ${safeHTML(row.tipoPred)}
                </div>
              </div>
              <div class="right">
                <div>Sessões: <b>${safeHTML(row.totalSessoes)}</b></div>
                <div>Total pago: <b>${safeHTML(brl(row.valorTotalPago))}</b></div>
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>Data/Hora</th>
                  <th>Tipo</th>
                  <th>Status</th>
                  <th style="text-align:right;">Duração</th>
                  <th style="text-align:right;">Valor</th>
                  <th>Serviço</th>
                </tr>
              </thead>

              <tbody>
                ${sessoesHtml || `<tr><td colspan="6">Sem sessões no período.</td></tr>`}
              </tbody>
            </table>
          </section>
        `;
      })
      .join("");

    const html = `
      <!doctype html>
      <html lang="pt-BR">
        <head>
          <meta charset="utf-8" />
          <title>${safeHTML(title)}</title>
          <style>
            @page { size: A4; margin: 14mm; }
            * { box-sizing: border-box; }
            body { font-family: Arial, sans-serif; color:#0f172a; margin: 0; }
            .top { display:flex; justify-content:space-between; align-items:flex-end; gap: 14px; margin-bottom:12px; }
            .h1 { font-size:19px; font-weight:900; }
            .sub { font-size:12px; color:#475569; margin-top: 4px; }
            .box { border:1px solid #e2e8f0; border-radius:12px; padding:10px; margin-bottom:14px; display:flex; justify-content:space-between; gap: 12px; background: #f8fafc; }
            .box b { font-size:13px; }
            .card { border:1px solid #e2e8f0; border-radius:12px; padding:10px; margin:0 0 12px; break-inside: avoid; }
            .cardHead { display:flex; justify-content:space-between; gap:12px; margin-bottom:8px; }
            .name { font-size:14px; font-weight:900; }
            .meta { font-size:12px; color:#475569; margin-top:3px; }
            .right { text-align:right; font-size:12px; color:#334155; line-height: 1.55; }
            table { width:100%; border-collapse:collapse; margin-top:8px; }
            th, td { border:1px solid #e2e8f0; padding:6px; font-size:11px; vertical-align: top; }
            th { background:#f1f5f9; text-align:left; }
            .footer { margin-top:10px; font-size:11px; color:#64748b; }
          </style>
        </head>

        <body>
          <div class="top">
            <div>
              <div class="h1">${safeHTML(title)}</div>
              <div class="sub">Período: ${safeHTML(periodo)}</div>
            </div>
            <div class="sub">Gerado em: ${safeHTML(generatedAt)}</div>
          </div>

          <div class="box">
            <div>Pacientes: <b>${safeHTML(totals.clientes)}</b></div>
            <div>Sessões: <b>${safeHTML(totals.totalSessoes)}</b></div>
            <div>Total: <b>${safeHTML(brl(totals.totalValor))}</b></div>
          </div>

          ${rowsHtml}

          <div class="footer">PsyAssist • Relatório Receita Saúde</div>
        </body>
      </html>
    `;

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.style.visibility = "hidden";

    document.body.appendChild(iframe);

    const doc = iframe.contentWindow?.document;

    if (!doc) {
      document.body.removeChild(iframe);
      toast.error({
        title: "PDF indisponível",
        message: "Não foi possível gerar o PDF neste navegador.",
      });
      return;
    }

    doc.open();
    doc.write(html);
    doc.close();

    iframe.onload = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } finally {
        setTimeout(() => {
          try {
            document.body.removeChild(iframe);
          } catch {
            // noop
          }
        }, 1000);
      }
    };
  };

  return (
    <div className="saude-page">
      <div className="saude-bg" aria-hidden="true">
        <span className="saude-bg__orb saude-bg__orb--one" />
        <span className="saude-bg__orb saude-bg__orb--two" />
        <span className="saude-bg__orb saude-bg__orb--three" />
      </div>

      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="saude-topbar">
        <button
          className="saude-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          <Menu size={19} aria-hidden="true" />
        </button>

        <div className="saude-brand">
          <img src={Logo} alt="PsyAssist" className="saude-brand__logo" />
          <div className="saude-brand__text">
            <strong>Receita Saúde</strong>
            <span>Financeiro • Relatório fiscal • {nomeUsuario || "Usuário"}</span>
          </div>
        </div>

        <div className="saude-top-actions">
          <button
            className="saude-btn saude-btn--ghost saude-btn--desktop"
            type="button"
            onClick={goBack}
          >
            <ArrowLeft size={16} />
            Financeiro
          </button>

          <button
            className="saude-btn saude-btn--ghost"
            type="button"
            onClick={exportarPDF}
            disabled={!rows.length || loading}
          >
            <FileText size={16} />
            Gerar PDF
          </button>

          <button
            className="saude-btn saude-btn--primary"
            type="button"
            onClick={reload}
            disabled={loading || refreshing}
          >
            <RefreshCw
              size={16}
              className={refreshing ? "saude-spin" : ""}
              aria-hidden="true"
            />
            {refreshing ? "Atualizando..." : "Atualizar"}
          </button>
        </div>
      </header>

      <main className="saude-main">
        {loading ? (
          <ReportSkeleton />
        ) : (
          <>
            <section className="saude-hero">
              <div>
                <span className="saude-kicker">
                  <ShieldCheck size={14} aria-hidden="true" />
                  Relatório fiscal
                </span>

                <h1>Receita Saúde</h1>

                <p>
                  {firstName ? `${firstName}, ` : ""}
                  consulte os recebimentos por paciente, identifique cliente e
                  pagador e gere o relatório do período sem misturar essa
                  obrigação fiscal com a gestão financeira do dia a dia.
                </p>
              </div>

              <HealthCard total={totals.totalValor} sessions={totals.totalSessoes} />
            </section>

            <section className="saude-kpis">
              <MetricCard
                icon={Users}
                label="Pacientes"
                value={totals.clientes}
                description="Com sessões no período"
                tone="blue"
              />

              <MetricCard
                icon={CalendarDays}
                label="Sessões"
                value={totals.totalSessoes}
                description="Total de atendimentos"
                tone="green"
              />

              <MetricCard
                icon={CircleDollarSign}
                label="Total pago"
                value={brl(totals.totalValor)}
                description={`Média por sessão: ${brl(totals.mediaPorSessao)}`}
                tone="violet"
              />
            </section>

            <section className="saude-fiscal-note">
              <div className="saude-fiscal-note__icon">
                <ShieldCheck size={18} aria-hidden="true" />
              </div>

              <div>
                <strong>Área fiscal do financeiro</strong>
                <span>
                  Esta tela usa os dados das sessões para organizar o relatório
                  Receita Saúde. Entradas, despesas, saldo e análises continuam
                  centralizados em /finance.
                </span>
              </div>
            </section>

            <section className="saude-panel">
              <div className="saude-panel__head">
                <div>
                  <span>Filtros</span>
                  <h2>Período do relatório</h2>
                  <p>
                    Escolha o intervalo para buscar os dados de Receita Saúde.
                  </p>
                </div>

                {lastUpdatedAt ? (
                  <div className="saude-updated">
                    Atualizado às {getTimeBR(lastUpdatedAt)}
                  </div>
                ) : null}
              </div>

              <div className="saude-filters">
                <label className="saude-field">
                  <span>Data início</span>
                  <input
                    type="date"
                    value={dataInicio}
                    onChange={(event) => setDataInicio(event.target.value)}
                  />
                </label>

                <label className="saude-field">
                  <span>Data fim</span>
                  <input
                    type="date"
                    value={dataFim}
                    onChange={(event) => setDataFim(event.target.value)}
                  />
                </label>

                <label className="saude-field">
                  <span>Pagamento</span>
                  <select
                    value={somentePagas ? "true" : "todos"}
                    onChange={(event) => setSomentePagas(event.target.value === "true")}
                  >
                    <option value="true">Somente pagas</option>
                    <option value="todos">Todas</option>
                  </select>
                </label>

                <label className="saude-field">
                  <span>Tipo</span>
                  <select value={tipoSessao} onChange={(event) => setTipoSessao(event.target.value)}>
                    <option value="todos">Todos</option>
                    <option value="Online">Online</option>
                    <option value="Presencial">Presencial</option>
                  </select>
                </label>

                <button
                  className="saude-btn saude-btn--primary"
                  type="button"
                  onClick={reload}
                  disabled={loading || refreshing}
                >
                  <Search size={16} />
                  Buscar
                </button>

                <button
                  className="saude-btn saude-btn--ghost-dark"
                  type="button"
                  onClick={exportarPDF}
                  disabled={!rows.length || loading}
                >
                  <FileText size={16} />
                  Gerar PDF
                </button>
              </div>
            </section>

            {error ? (
              <EmptyState
                title="Não foi possível carregar o relatório."
                message={error}
                onRetry={reload}
                onBack={goBack}
                isError
              />
            ) : !rows.length ? (
              <EmptyState
                title="Nenhum dado encontrado."
                message="Não encontramos sessões no período informado. Altere as datas e tente novamente."
                onRetry={reload}
                onBack={goBack}
              />
            ) : (
              <section className="saude-panel">
                <div className="saude-panel__head saude-panel__head--with-tools">
                  <div>
                    <span>Resultados</span>
                    <h2>Pacientes e valores</h2>
                    <p>
                      Clique em sessões ou em detalhes para ver a composição do
                      valor por atendimento.
                    </p>
                  </div>

                  <div className="saude-counter">
                    {filteredRows.length} de {rows.length}
                  </div>
                </div>

                <div className="saude-toolbar">
                  <label className="saude-search">
                    <span aria-hidden="true">
                      <Search size={16} />
                    </span>
                    <input
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Buscar por paciente, CPF do cliente, CPF do pagador ou tipo..."
                    />

                    {search ? (
                      <button
                        type="button"
                        onClick={() => setSearch("")}
                        aria-label="Limpar busca"
                      >
                        <X size={14} />
                      </button>
                    ) : null}
                  </label>

                  <div className="saude-total-chip">
                    <span>Sessões filtradas</span>
                    <strong>{filteredTotals.sessoes}</strong>
                  </div>

                  <div className="saude-total-chip">
                    <span>Total filtrado</span>
                    <strong>{brl(filteredTotals.valor)}</strong>
                  </div>
                </div>

                {filteredRows.length === 0 ? (
                  <div className="saude-empty-mini">
                    Nenhum paciente encontrado com a busca atual.
                  </div>
                ) : (
                  <div className="saude-list">
                    {filteredRows.map((row) => (
                      <article className="saude-row" key={`${row.id}-${row.cpfPagador}`}>
                        <div className="saude-row__avatar" aria-hidden="true">
                          {getInitials(row.nome)}
                        </div>

                        <div className="saude-row__content">
                          <strong title={row.nome}>{row.nome}</strong>

                          <div className="saude-row__tags">
                            <span>CPF cliente: {formatCPF(row.cpfCliente)}</span>

                            {row.pagadorEhCliente ? (
                              <span>Pagador: próprio cliente</span>
                            ) : (
                              <span>CPF pagador: {formatCPF(row.cpfPagador)}</span>
                            )}

                            <button type="button" onClick={() => openDetails(row)}>
                              Sessões: {row.totalSessoes}
                            </button>

                            <span>Tipo: {row.tipoPred}</span>
                          </div>
                        </div>

                        <div className="saude-row__amount">
                          <strong>{brl(row.valorTotalPago)}</strong>
                          <span>acumulado</span>

                          <button type="button" onClick={() => openDetails(row)}>
                            Ver detalhes
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </section>
            )}

            <section className="saude-bottom-actions">
              <button
                className="saude-btn saude-btn--ghost-dark"
                type="button"
                onClick={goBack}
              >
                <ArrowLeft size={16} />
                Voltar ao financeiro
              </button>

              <button
                className="saude-btn saude-btn--primary"
                type="button"
                onClick={exportarPDF}
                disabled={!rows.length}
              >
                <FileText size={16} />
                Gerar PDF
              </button>
            </section>
          </>
        )}
      </main>

      <DetailsModal
        open={detailsOpen}
        client={detailsClient}
        dataInicio={dataInicio}
        dataFim={dataFim}
        onClose={closeDetails}
      />
    </div>
  );
}
